// ─────────────────────────────────────────────────────────────────────────────
// DesktopSwipeDragLayer — interactive, finger-tracked "drag to switch
// desktops" gesture. Mounted OUTSIDE the keyed DesktopPageWrapper (see
// App.tsx) so an in-flight drag survives the desktop switch it triggers —
// same reasoning as DesktopTransitionLayer.
//
// Unlike the old useDesktopSpaceSwipe (deleted) this never waits for a
// completed gesture before animating: every horizontal wheel tick past a
// few px moves the screen live, in lockstep with the input. Only the
// *release* (a short gap in wheel activity — wheel events carry no explicit
// "gesture end" signal) is threshold-gated, deciding whether to finish the
// switch or spring back.
//
// Visual model — two layers, scrubbed live by drag progress instead of
// played on a fixed timer, tiling the viewport with no gap at any point in
// the drag (the incoming pane's translateX is always the exact complement
// of the shell's):
//   - The current live DesktopShell (found via its `.desktop-shell-root`
//     class — the same class navigateWithDesktopSlide's sibling CSS path
//     targets) actually TRANSLATES fully off-screen in the drag direction.
//     No snapshot needed, it's already on screen; we just move the real node
//     — its own real background moves with it, so what's sliding away is
//     never anything but the actual current desktop.
//   - This layer's own incoming pane slides in from the opposite edge,
//     showing the target desktop's actual background (not the switcher's
//     miniature schematic mock — that's built for a 100x50 thumbnail and
//     would look like a toy blown up full-screen). Procedural mounts a live
//     `ProceduralBackground` instance rather than
//     `resolveBackgroundSwatchStyle`'s flat gradient approximation — that
//     approximation is visually nothing like an actual noise-based render
//     (e.g. Contours' thin isolines), so using it here read as sliding in a
//     completely different, wrong background right before the real one
//     snapped into place.
// Stacking relies on the real shell's own position, unmodified — see
// DesktopSwipeDragLayer.module.scss for why the pane's z-index is chosen
// relative to GUILayout's z-index:10 content wrapper instead of trying to
// boost the shell itself (which can't escape that wrapper's stacking context
// no matter its own z-index).
//
// On release: past ~35% of the viewport width, or a fast-enough flick,
// tweens the rest of the way and performs the real navigation (behind the
// now-fully-covering incoming pane, with the same cachedSpace warm path
// button clicks use), holds briefly, then fades away to reveal the real
// content. Short of that, tweens back to zero — no navigation, nothing else
// changes.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSystemStore from '../../../../stores/systemStore';
import useSpaceStore from '../../../../stores/spaceStore';
import { useSpatialUIStore } from '../../../../stores/spaceSpatialUIStore';
import type { SpaceBackground } from '../../../../types/spaces';
import ProceduralBackground from '../../backgrounds/ProceduralBackground';
import { resolveBackgroundSwatchStyle } from './desktopBackgroundSwatch';
import { emitDesktopHotkeyFeedback } from './desktopHotkeyFeedback';
import {
  buildSwitcherSpaces,
  clampSwipeOffset,
  computeSwipeTargetId,
  estimateSwipeVelocity,
  isBlockedByScrollableContent,
  isConfidentFlick,
  isDeliberateHorizontalIntent,
  isOverDesktopChrome,
  isPinchZoomWheelEvent,
  shouldCommitSwipeDrag,
  SWIPE_DRAG_IDLE_MS,
  SWIPE_DRAG_REVEAL_FADE_MS,
  SWIPE_DRAG_REVEAL_HOLD_MS,
  SWIPE_DRAG_SETTLE_MS,
  SWIPE_DRAG_START_PX,
  SwipeDirection,
  SwipeOffsetSample,
} from './desktopSwipeDrag';
import { waitForDesktopReady } from '../../../../utils/desktopSwitchReadiness';
import styles from './DesktopSwipeDragLayer.module.scss';

type DragPhase = 'idle' | 'dragging' | 'settling' | 'revealing';

interface DesktopSwipeDragLayerProps {
  /** The currently active desktop's id — undefined while resolving a route. */
  currentId: string | undefined;
}

const DesktopSwipeDragLayer: React.FC<DesktopSwipeDragLayerProps> = ({ currentId }) => {
  const navigate = useNavigate();
  const currentIdRef = useRef(currentId);
  currentIdRef.current = currentId;

  const [phase, setPhase] = useState<DragPhase>('idle');
  const [targetBackground, setTargetBackground] = useState<SpaceBackground | null | undefined>(null);
  const [revealFading, setRevealFading] = useState(false);

  const phaseRef = useRef<DragPhase>('idle');
  const directionRef = useRef<SwipeDirection>('next');
  const targetIdRef = useRef<string | null>(null);
  const targetSpaceRef = useRef<any>(null);

  const accumRef = useRef(0);
  const progressRef = useRef(0); // 0..1, always in the locked direction's sense
  const viewportWidthRef = useRef(0);
  const samplesRef = useRef<SwipeOffsetSample[]>([]);
  const lastEventMsRef = useRef(0);
  const idleTimerRef = useRef<number | null>(null);
  const settleRafRef = useRef<number | null>(null);
  const revealTimerRef = useRef<number | null>(null);
  // Invalidates a pending waitForDesktopReady(...).then(startFade) continuation
  // if the gesture finishes (or this layer unmounts) before that promise
  // resolves — otherwise a stray resolution from an already-finished gesture
  // could fire setRevealFading/finishGesture again for a later, unrelated one.
  const revealTokenRef = useRef(0);
  const unmountedRef = useRef(false);

  const overlayElRef = useRef<HTMLDivElement | null>(null);
  const realShellElRef = useRef<HTMLElement | null>(null);

  const applyProgress = (progress: number) => {
    const clamped = Math.max(0, Math.min(1, progress));
    progressRef.current = clamped;

    const sign = directionRef.current === 'next' ? 1 : -1;

    const overlayEl = overlayElRef.current;
    if (overlayEl) {
      overlayEl.style.transform = `translateX(${(1 - clamped) * 100 * sign}%)`;
    }

    // Exact complement of the incoming pane's transform above — the two
    // always tile the full viewport with no gap, at any progress.
    const shellEl = realShellElRef.current;
    if (shellEl) {
      shellEl.style.transform = `translateX(${-clamped * 100 * sign}%)`;
    }
  };

  const clearIdleTimer = () => {
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const clearSettleRaf = () => {
    if (settleRafRef.current !== null) {
      window.cancelAnimationFrame(settleRafRef.current);
      settleRafRef.current = null;
    }
  };

  const resetRealShellStyles = () => {
    if (realShellElRef.current) {
      realShellElRef.current.style.transform = '';
      realShellElRef.current.style.pointerEvents = '';
    }
    realShellElRef.current = null;
  };

  const finishGesture = () => {
    clearIdleTimer();
    clearSettleRaf();
    if (revealTimerRef.current !== null) {
      window.clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }
    revealTokenRef.current += 1;
    accumRef.current = 0;
    progressRef.current = 0;
    samplesRef.current = [];
    targetIdRef.current = null;
    targetSpaceRef.current = null;
    resetRealShellStyles();
    setRevealFading(false);
    phaseRef.current = 'idle';
    setPhase('idle');
  };

  const settleTo = (target: 0 | 1, onDone: () => void) => {
    phaseRef.current = 'settling';
    setPhase('settling');
    const startProgress = progressRef.current;
    const startMs = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startMs;
      const t = Math.min(1, elapsed / SWIPE_DRAG_SETTLE_MS);
      // Ease-out — quick to respond, gentle landing.
      const eased = 1 - (1 - t) * (1 - t);
      applyProgress(startProgress + (target - startProgress) * eased);
      if (t < 1) {
        settleRafRef.current = window.requestAnimationFrame(tick);
      } else {
        settleRafRef.current = null;
        onDone();
      }
    };
    settleRafRef.current = window.requestAnimationFrame(tick);
  };

  const commitDrag = () => {
    const targetId = targetIdRef.current;
    const targetSpace = targetSpaceRef.current;
    settleTo(1, () => {
      phaseRef.current = 'revealing';
      setPhase('revealing');
      // Real content is now fully covered by our own overlay — no need to
      // keep touching the outgoing shell's styles at all from here on.
      realShellElRef.current = null;

      const revealToken = ++revealTokenRef.current;
      const startFade = () => {
        // A newer gesture already finished/superseded this one, or this
        // layer unmounted while we were waiting — don't touch state.
        if (unmountedRef.current || revealTokenRef.current !== revealToken) return;
        setRevealFading(true);
        revealTimerRef.current = window.setTimeout(() => {
          if (unmountedRef.current || revealTokenRef.current !== revealToken) return;
          finishGesture();
        }, SWIPE_DRAG_REVEAL_FADE_MS);
      };

      if (targetId) {
        emitDesktopHotkeyFeedback({ label: `Switched to ${targetSpace?.name || 'desktop'}`, keys: [] });
        // viewTransitionActive suppresses DesktopShell's own CSS slide-in
        // fallback — this drag already performed the slide manually.
        navigate(`/desktops/${targetId}`, { state: { cachedSpace: targetSpace, viewTransitionActive: true } });
        // Hold the overlay until the target Desktop has actually landed and
        // painted — bounded at SWIPE_DRAG_REVEAL_HOLD_MS so a cold target
        // never holds longer than the fixed wait this replaces (see
        // waitForDesktopReady's docstring).
        waitForDesktopReady(targetId, SWIPE_DRAG_REVEAL_HOLD_MS).then(startFade);
      } else {
        // No target resolved (shouldn't normally happen at this point) —
        // nothing to gate readiness on, fall back to the fixed hold.
        revealTimerRef.current = window.setTimeout(startFade, SWIPE_DRAG_REVEAL_HOLD_MS);
      }
    });
  };

  const cancelDrag = () => {
    settleTo(0, finishGesture);
  };

  const handleRelease = () => {
    if (phaseRef.current !== 'dragging') return;
    const now = performance.now();
    const velocity = estimateSwipeVelocity(samplesRef.current, now);
    const offsetPx = progressRef.current * viewportWidthRef.current;
    if (shouldCommitSwipeDrag(offsetPx, viewportWidthRef.current, velocity)) {
      commitDrag();
    } else {
      cancelDrag();
    }
  };

  const scheduleIdleCheck = () => {
    clearIdleTimer();
    idleTimerRef.current = window.setTimeout(handleRelease, SWIPE_DRAG_IDLE_MS);
  };

  const feedDrag = (deltaX: number) => {
    const sign = directionRef.current === 'next' ? 1 : -1;
    accumRef.current += deltaX;
    const magnitude = accumRef.current * sign;
    const offsetPx = clampSwipeOffset(magnitude, viewportWidthRef.current);
    const progress = viewportWidthRef.current > 0 ? offsetPx / viewportWidthRef.current : 0;
    applyProgress(progress);
    samplesRef.current.push({ offsetPx, atMs: performance.now() });
    // Bound memory — only a short trailing window is ever needed for the
    // release velocity estimate.
    if (samplesRef.current.length > 60) {
      samplesRef.current.splice(0, samplesRef.current.length - 60);
    }
  };

  const startDrag = (dir: SwipeDirection, targetId: string, targetSpace: any) => {
    directionRef.current = dir;
    targetIdRef.current = targetId;
    targetSpaceRef.current = targetSpace;
    viewportWidthRef.current = window.innerWidth;
    progressRef.current = 0;
    samplesRef.current = [];

    realShellElRef.current = document.querySelector<HTMLElement>('.desktop-shell-root');
    if (realShellElRef.current) {
      realShellElRef.current.style.pointerEvents = 'none';
    }

    setTargetBackground(targetSpace?.settings?.background ?? null);
    phaseRef.current = 'dragging';
    setPhase('dragging');

    // Apply the delta that armed this drag as its starting progress —
    // accumRef.current still holds it (the caller deliberately does not
    // reset it before calling startDrag) — a large initial tick (a flick)
    // should count toward the drag distance immediately rather than being
    // discarded just for detecting that a drag should start.
    feedDrag(0);
  };

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (phaseRef.current === 'settling' || phaseRef.current === 'revealing') return;
      const activeSpace = useSpaceStore.getState().activeSpace;
      const viewMode = (activeSpace?.settings as any)?.viewMode;
      // spatialCanvas already uses horizontal wheel deltas for canvas panning.
      if (viewMode === 'spatialCanvas') return;

      const now = performance.now();

      if (phaseRef.current === 'idle') {
        // Pinch-to-zoom dispatches as a ctrlKey wheel event — unrelated to
        // switching desktops, must never contribute to the accumulator.
        if (isPinchZoomWheelEvent(e)) {
          accumRef.current = 0;
          return;
        }
        if (!isDeliberateHorizontalIntent(e.deltaX, e.deltaY)) return;
        // Never arm the whole-screen slide over an application (a window, the
        // chat panel, dock/sidebar/system bar/quick bar) — only the bare
        // desktop background may start this gesture, regardless of whether
        // an inner scroller underneath the pointer is already at its edge.
        // While the Mission-Control window overview is open, its backdrop and
        // the live-scaled windows tiled into it count as that "bare
        // background" too (see isOverDesktopChrome's overview-mode note).
        const overviewActive = useSpatialUIStore.getState().overviewMode;
        if (isOverDesktopChrome(e.target, overviewActive)) {
          accumRef.current = 0;
          return;
        }
        if (isBlockedByScrollableContent(e.target as Element, e.deltaX)) {
          accumRef.current = 0;
          return;
        }
        if (now - lastEventMsRef.current > SWIPE_DRAG_IDLE_MS) {
          accumRef.current = 0;
        }
        lastEventMsRef.current = now;
        accumRef.current += e.deltaX;

        if (Math.abs(accumRef.current) < SWIPE_DRAG_START_PX) return;

        const id = currentIdRef.current;
        if (!id) {
          accumRef.current = 0;
          return;
        }
        const dir: SwipeDirection = accumRef.current > 0 ? 'next' : 'previous';
        const recentSpaces = useSystemStore.getState().recentSpaces;
        const switcherSpaces = buildSwitcherSpaces(recentSpaces as any[], activeSpace as any);
        const orderedIds = switcherSpaces.map((s: any) => s.id);
        const targetId = computeSwipeTargetId(orderedIds, id, dir);
        if (!targetId) {
          accumRef.current = 0;
          return;
        }
        const targetSpace = switcherSpaces.find((s: any) => s.id === targetId) ?? null;
        // accumRef.current still holds the delta that armed this drag —
        // startDrag applies it as the drag's starting progress rather than
        // discarding it.
        startDrag(dir, targetId, targetSpace);
        scheduleIdleCheck();
        return;
      }

      // phase === 'dragging'
      // Same as the idle-phase check above — a pinch-to-zoom gesture mid-drag
      // must not perturb the swipe's progress. Skip the event entirely rather
      // than resetting accumRef.current here — unlike the idle phase, it's
      // already holding the drag's actual accumulated offset, not just an
      // arming threshold.
      if (isPinchZoomWheelEvent(e)) return;
      lastEventMsRef.current = now;
      feedDrag(e.deltaX);

      // Commit the instant a flick is unambiguous, rather than waiting for
      // the idle-release timer — a decaying trackpad momentum tail can keep
      // re-arming that timer for up to ~1s after the real flick already
      // ended, which is what read as the screen sliding partway, hanging,
      // then snapping the rest of the way. See isConfidentFlick's own doc
      // for why this only ever fires early for velocity, never distance.
      const velocity = estimateSwipeVelocity(samplesRef.current, now);
      if (isConfidentFlick(progressRef.current * viewportWidthRef.current, velocity)) {
        clearIdleTimer();
        commitDrag();
        return;
      }

      scheduleIdleCheck();
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      window.removeEventListener('wheel', handleWheel);
      clearIdleTimer();
      clearSettleRaf();
      // If this layer unmounts mid-gesture (route/App-level remount), the
      // `pointer-events: none` startDrag applied to `.desktop-shell-root`
      // would otherwise never get cleared — locking every window and the
      // desktop background click-through with no gesture left running to
      // finish the job.
      resetRealShellStyles();
      // Guards a pending waitForDesktopReady(...).then(startFade) — without
      // this, that promise resolving after unmount would call setState on an
      // unmounted component.
      unmountedRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only the bare 'dragging' phase is safe to force-finish here: nothing has
  // been decided yet, so snapping straight back to idle just discards an
  // in-progress preview — the `pointer-events: none` startDrag applied to
  // the real shell would otherwise sit there for as long as the tab stays
  // hidden if the release never comes (e.g. the gesture's last wheel tick
  // landed right as focus left the tab). 'settling' and 'revealing' both
  // mean a commit-vs-cancel decision has ALREADY been made — 'settling'
  // toward a commit still has the real `navigate()` call queued inside its
  // `settleTo` tween's `onDone`, so force-finishing here would cancel that
  // rAF loop before `onDone` ever runs, silently swallowing a desktop switch
  // the user already committed to. Those tweens' own rAF simply resumes and
  // completes normally once the tab is visible again — the pointer-events
  // lock they hold is moot while the tab isn't visible to interact with.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && phaseRef.current === 'dragging') {
        finishGesture();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Always rendered (never conditionally unmounted) so overlayElRef stays
  // attached across the whole component lifetime — startDrag applies the
  // first frame of the transform synchronously, before React has had a
  // chance to commit a phase-driven re-render.
  const idle = phase === 'idle';
  const hiddenStyle = {
    opacity: idle || revealFading ? 0 : 1,
    transition: revealFading ? `opacity ${SWIPE_DRAG_REVEAL_FADE_MS}ms ease-out` : undefined,
  };
  const isTargetProcedural = targetBackground?.type === 'procedural';
  return (
    <div
      ref={overlayElRef}
      className={styles.pane}
      data-testid="desktop-swipe-drag-overlay"
      data-phase={phase}
      style={hiddenStyle}
      aria-hidden
    >
      {isTargetProcedural && targetBackground ? (
        <div className={styles.swatch}>
          <ProceduralBackground background={targetBackground} initialPan={{ x: 0, y: 0 }} initialZoom={1} />
        </div>
      ) : (
        <div className={styles.swatch} style={resolveBackgroundSwatchStyle(targetBackground)} />
      )}
    </div>
  );
};

export default DesktopSwipeDragLayer;
